import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { DatePickerFr } from '../ui/DatePickerFr';

import { BarChart3, FileSpreadsheet, Loader2, Repeat, CheckCircle, PieChart, Euro, Calendar, CalendarClock, RefreshCw, TrendingUp, Wallet, Building2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';

const _RECHARTS_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function ComptabiliteTab({
  calculatedComptaStats, exportLoading,
  exportAcomptes, exportEcheanciers, exportContributionsAcquittees,
  exportPaiementsParMode, exportPaiementsSEPA, exportExcelComplet,
  filterDateDebut, setFilterDateDebut, filterDateFin, setFilterDateFin,
  allDocuments, calculateMontantTTC
}) {
  return (
        <div className="space-y-6" data-testid="comptabilite-content">
          {/* Header Premium avec filtres de période */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 px-8 py-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Tableau de Bord Comptable</h3>
                    <p className="text-cyan-100 text-sm mt-1">Statistiques et exports des contributions ESS</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20">
                    <Calendar className="h-5 w-5 text-white" />
                    <DatePickerFr
                      value={filterDateDebut}
                      onChange={setFilterDateDebut}
                      placeholder="Début"
                      className="w-36 border-0"
                    />
                    <span className="text-white font-bold">→</span>
                    <DatePickerFr
                      value={filterDateFin}
                      onChange={setFilterDateFin}
                      placeholder="Fin"
                      className="w-36 border-0"
                    />
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
                    onClick={() => { setFilterDateDebut(''); setFilterDateFin(''); }}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Tout
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* KPIs Comptables - Design Premium */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-emerald-100 text-xs font-medium tracking-wide uppercase">Total Encaissé</p>
                    <p className="text-3xl font-black tracking-tight">
                      {calculatedComptaStats.totalEncaisse.toLocaleString('fr-FR')}
                      <span className="text-lg ml-1 font-normal text-emerald-200">€</span>
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Euro className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-amber-100 text-xs font-medium tracking-wide uppercase">Total Acomptes</p>
                    <p className="text-3xl font-black tracking-tight">
                      {calculatedComptaStats.totalAcomptes.toLocaleString('fr-FR')}
                      <span className="text-lg ml-1 font-normal text-amber-200">€</span>
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Wallet className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-blue-100 text-xs font-medium tracking-wide uppercase">Contributions Acquittées</p>
                    <p className="text-3xl font-black tracking-tight">
                      {calculatedComptaStats.contributionsAcquittees}
                      <span className="text-lg ml-1 font-normal text-blue-200">dossiers</span>
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-purple-100 text-xs font-medium tracking-wide uppercase">Taux de Recouvrement</p>
                    <p className="text-3xl font-black tracking-tight">
                      {((calculatedComptaStats.totalEncaisse / (calculatedComptaStats.totalFacture || 1)) * 100).toFixed(1)}
                      <span className="text-lg ml-1 font-normal text-purple-200">%</span>
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique évolution */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Évolution des Encaissements
                </h4>
              </div>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart
                    data={(() => {
                      const monthlyData = {};
                      const now = new Date();
                      for (let i = 5; i >= 0; i--) {
                        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const key = d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
                        monthlyData[key] = { mois: key, encaisse: 0, facture: 0 };
                      }
                      allDocuments.forEach(doc => {
                        const date = new Date(doc.date_creation || doc.created_at);
                        const key = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
                        if (monthlyData[key]) {
                          monthlyData[key].facture += calculateMontantTTC(doc) || 0;
                          if (doc.paiement_valide || doc.est_solde) {
                            monthlyData[key].encaisse += calculateMontantTTC(doc) || 0;
                          }
                        }
                      });
                      return Object.values(monthlyData);
                    })()}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorEncaisseESS" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="mois" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                    <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
                    <Area type="monotone" dataKey="encaisse" stroke="#10b981" fillOpacity={1} fill="url(#colorEncaisseESS)" name="Encaissé" strokeWidth={2} />
                    <Area type="monotone" dataKey="facture" stroke="#6366f1" fill="transparent" name="Facturé" strokeWidth={2} strokeDasharray="5 5" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Graphique répartition par mode */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Répartition par Mode de Paiement
                </h4>
              </div>
              <CardContent className="pt-6">
                {(() => {
                  const pieData = [
                    { name: 'Virement', value: calculatedComptaStats.virement.montant, count: calculatedComptaStats.virement.count, color: '#6366f1' },
                    { name: 'SEPA Core', value: calculatedComptaStats.sepaCore.montant, count: calculatedComptaStats.sepaCore.count, color: '#3b82f6' },
                    { name: 'SEPA B2B', value: calculatedComptaStats.sepaB2B.montant, count: calculatedComptaStats.sepaB2B.count, color: '#0ea5e9' },
                    { name: 'CB Stripe', value: calculatedComptaStats.cb.montant, count: calculatedComptaStats.cb.count, color: '#10b981' },
                    { name: 'Chèque', value: calculatedComptaStats.cheque.montant, count: calculatedComptaStats.cheque.count, color: '#f59e0b' },
                    { name: 'Espèces', value: calculatedComptaStats.especes?.montant || 0, count: calculatedComptaStats.especes?.count || 0, color: '#ef4444' }
                  ].filter(d => d.value > 0);
                  
                  if (pieData.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center h-[280px] text-gray-400">
                        <PieChart className="h-16 w-16 mb-4 opacity-30" />
                        <p className="text-lg font-medium">Aucune donnée de paiement</p>
                        <p className="text-sm mt-1">Les données apparaîtront après validation des paiements</p>
                      </div>
                    );
                  }
                  
                  return (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name) => [`${value.toLocaleString('fr-FR')} €`, name]}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                      {/* Légende custom */}
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        {pieData.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{item.name}</p>
                              <p className="text-xs text-gray-500">{item.count} paiement(s)</p>
                            </div>
                            <p className="text-sm font-bold text-gray-900">{item.value.toLocaleString('fr-FR')} €</p>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Statistiques détaillées SEPA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="shadow-lg border-2 border-blue-200">
              <CardContent className="pt-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Repeat className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">SEPA Core (Particuliers)</h4>
                    <p className="text-sm text-gray-500">Notification J-14, remboursement possible</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Prélèvements</p>
                    <p className="text-2xl font-bold text-blue-800">{calculatedComptaStats.sepaCore.count}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Montant total</p>
                    <p className="text-2xl font-bold text-blue-800">{calculatedComptaStats.sepaCore.montant.toLocaleString('fr-FR')} €</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-lg border-2 border-indigo-200">
              <CardContent className="pt-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-indigo-100 rounded-xl">
                    <Building2 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">SEPA B2B (Entreprises)</h4>
                    <p className="text-sm text-gray-500">Notification J-1, pas de remboursement</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-600 font-medium">Prélèvements</p>
                    <p className="text-2xl font-bold text-indigo-800">{calculatedComptaStats.sepaB2B.count}</p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-600 font-medium">Montant total</p>
                    <p className="text-2xl font-bold text-indigo-800">{calculatedComptaStats.sepaB2B.montant.toLocaleString('fr-FR')} €</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section Exports */}
          <Card className="shadow-lg border-2 border-emerald-200">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
              <h4 className="text-white font-semibold flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Exports Comptables
              </h4>
            </div>
            <CardContent className="pt-5">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 hover:bg-amber-50 hover:border-amber-300" onClick={exportAcomptes} disabled={exportLoading}>
                  <Wallet className="h-5 w-5 text-amber-600" />
                  <span className="text-xs font-medium">Acomptes CSV</span>
                </Button>
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300" onClick={exportEcheanciers} disabled={exportLoading}>
                  <CalendarClock className="h-5 w-5 text-purple-600" />
                  <span className="text-xs font-medium">Échéanciers CSV</span>
                </Button>
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300" onClick={exportContributionsAcquittees} disabled={exportLoading}>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-xs font-medium">Acquittées CSV</span>
                </Button>
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 hover:bg-indigo-50 hover:border-indigo-300" onClick={exportPaiementsParMode} disabled={exportLoading}>
                  <PieChart className="h-5 w-5 text-indigo-600" />
                  <span className="text-xs font-medium">Par Mode CSV</span>
                </Button>
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300" onClick={exportPaiementsSEPA} disabled={exportLoading}>
                  <Repeat className="h-5 w-5 text-blue-600" />
                  <span className="text-xs font-medium">SEPA CSV</span>
                </Button>
                <Button className="h-auto py-3 flex flex-col items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" onClick={exportExcelComplet} disabled={exportLoading}>
                  {exportLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileSpreadsheet className="h-5 w-5" />}
                  <span className="text-xs font-medium">Excel Complet</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
  );

}
